import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
    Briefcase, Clock, CheckCircle2, AlertCircle,
    AlertTriangle, ChevronRight,
    Eye, FileText, Palette, Building2
} from 'lucide-react';
import axios from 'axios';

import { API_BASE } from '../lib/apiBase';
import { DemoBadge } from '../components/DemoBadge';
import { PartnerSettlementView } from '../components/PartnerSettlementView';
const API = API_BASE;

const STATUS_CONFIG = {
    'Open': { label: 'Open', color: 'bg-slate-100 text-slate-700', icon: Clock },
    'Assigned': { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: Briefcase },
    'In_Progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Clock },
    'Awaiting_Approval': { label: 'Awaiting Approval', color: 'bg-purple-100 text-purple-700', icon: AlertCircle },
    'Approved': { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    'Completed': { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

// Per-verb config for the Needs Attention panel and the Action column.
// Each verb is either partner-actionable (`side: 'partner'`) or a
// waiting state (`side: 'customer'`). Keeps the panel bucketing and
// per-row label using the same source of truth so a single label
// change here updates both places. ``dotClass`` is a full class name
// so Tailwind's JIT compiler picks it up.
const ACTION_VERBS = {
    review_new_customer_file: {
        label: 'Review new customer file',
        side: 'partner',
        dotClass: 'bg-rose-500',
    },
    address_change_request: {
        label: 'Respond to customer changes',
        side: 'partner',
        dotClass: 'bg-rose-500',
    },
    upload_corrected_file: {
        label: 'Upload corrected file',
        side: 'partner',
        dotClass: 'bg-amber-500',
    },
    submit_for_customer_approval: {
        label: 'Submit for customer approval',
        side: 'partner',
        dotClass: 'bg-amber-500',
    },
    deliver_to_factory: {
        label: 'Deliver to factory',
        side: 'partner',
        dotClass: 'bg-emerald-500',
    },
    waiting_for_customer_file: {
        label: 'Waiting for customer file',
        side: 'customer',
        dotClass: 'bg-sky-500',
    },
    waiting_for_customer_approval: {
        label: 'Waiting for customer approval',
        side: 'customer',
        dotClass: 'bg-sky-500',
    },
};

export default function ReproPortalPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    // ``verbFilter`` is set when the user clicks an item inside the
    // Needs Attention panel, mirroring the admin dashboard pattern:
    // the panel drives a filtered view of the same table below.
    // ``null`` = no filter.
    const [verbFilter, setVerbFilter] = useState(null);
    const [view, setView] = useState('active');  // 'active' | 'completed' | 'settlement'
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    const fetchJobs = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            // Use my-jobs endpoint for repro partners
            const endpoint = user?.role === 'repro_partner' 
                ? `${API}/api/repro-jobs/my-jobs`
                : `${API}/api/repro-jobs`;
            
            const response = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setJobs(response.data);
        } catch (error) {
            toast.error('Failed to load jobs');
        } finally {
            setLoading(false);
        }
    }, [user?.role]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    // Tab-scoped jobs: 'active' = everything except Completed, 'completed' = only Completed
    const scopedJobs = view === 'completed'
        ? jobs.filter(j => j.status === 'Completed')
        : jobs.filter(j => j.status !== 'Completed');

    let filteredJobs = statusFilter === 'all'
        ? scopedJobs
        : scopedJobs.filter(j => j.status === statusFilter);
    if (verbFilter) {
        filteredJobs = filteredJobs.filter(j => j.next_action === verbFilter);
    }

    const activeJobs = jobs.filter(j => j.status !== 'Completed').length;
    const completedJobs = jobs.filter(j => j.status === 'Completed').length;

    // Group active jobs by verb — one entry per verb the partner (or
    // customer) needs to act on. Deterministic ordering keeps the
    // panel from jumping around between fetches. `Approved` jobs are
    // NOT skipped: they still carry the `deliver_to_factory` verb until
    // the partner clicks "Mark as Delivered to Factory".
    const verbGroups = useMemo(() => {
        const groups = {};
        for (const j of jobs) {
            if (j.status === 'Completed') continue;
            if (!j.next_action) continue;
            (groups[j.next_action] ||= []).push(j);
        }
        return groups;
    }, [jobs]);

    const partnerVerbs = Object.keys(ACTION_VERBS).filter(v => ACTION_VERBS[v].side === 'partner');
    const customerVerbs = Object.keys(ACTION_VERBS).filter(v => ACTION_VERBS[v].side === 'customer');
    const totalNeedsAttention = partnerVerbs.reduce((n, v) => n + (verbGroups[v]?.length || 0), 0);
    const totalWaitingCustomer = customerVerbs.reduce((n, v) => n + (verbGroups[v]?.length || 0), 0);

    // Status options shown in dropdown are scoped to the current tab
    const visibleStatusKeys = Object.keys(STATUS_CONFIG).filter(k =>
        view === 'completed' ? k === 'Completed' : k !== 'Completed'
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="container mx-auto px-4 py-6 max-w-7xl">
                {/* Page Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Artwork Jobs</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {user?.partner_name ? `Jobs for ${user.partner_name}` : 'Manage artwork correction jobs'}
                        </p>
                    </div>
                </div>

                {/* Needs Attention Panel — same pattern as the Admin Orders
                    page. Two columns: partner-actionable verbs on the left,
                    customer-side waiting states on the right. Each item
                    filters the table below to the exact job(s) with that
                    next_action, or (when there's only one) navigates
                    directly to it. */}
                {view === 'active' && (totalNeedsAttention + totalWaitingCustomer) > 0 && (
                    <Card className="mb-6 border-amber-200 bg-amber-50/40" data-testid="partner-attention-panel">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                                Needs Attention
                                <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-700 border-amber-300">
                                    {totalNeedsAttention + totalWaitingCustomer}
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className={`grid grid-cols-1 ${totalNeedsAttention > 0 && totalWaitingCustomer > 0 ? 'md:grid-cols-2' : ''} gap-6`}>
                                {totalNeedsAttention > 0 && (
                                    <div className="flex flex-col gap-2" data-testid="attention-col-partner">
                                        <div className="flex items-center gap-2 px-1">
                                            <div className="h-2 w-2 rounded-full bg-rose-500"></div>
                                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">Needs Attention</h4>
                                            <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 h-4">{totalNeedsAttention}</Badge>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {partnerVerbs.map(verb => {
                                                const group = verbGroups[verb] || [];
                                                if (group.length === 0) return null;
                                                const cfg = ACTION_VERBS[verb];
                                                const onClick = () => {
                                                    if (group.length === 1) {
                                                        // Deep-link to the specific
                                                        // file when the backend tagged
                                                        // one. Falls back to the job
                                                        // page otherwise.
                                                        const job = group[0];
                                                        const url = `/artwork-jobs/${job.id}`;
                                                        const hash = job.next_action_target_file_id
                                                            ? `#file-${job.next_action_target_file_id}`
                                                            : '';
                                                        navigate(url + hash);
                                                    } else {
                                                        setView('active');
                                                        setStatusFilter('all');
                                                        setVerbFilter(verb);
                                                    }
                                                };
                                                return (
                                                    <button
                                                        key={verb}
                                                        type="button"
                                                        onClick={onClick}
                                                        className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-colors text-sm text-left"
                                                        data-testid={`attention-verb-${verb}`}
                                                    >
                                                        <div className={`h-2 w-2 rounded-full ${cfg.dotClass} flex-shrink-0`}></div>
                                                        <span className="text-slate-700 flex-1">
                                                            <span className="font-medium">{group.length}</span>{' '}
                                                            {cfg.label.toLowerCase()}
                                                        </span>
                                                        <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {totalWaitingCustomer > 0 && (
                                    <div className="flex flex-col gap-2" data-testid="attention-col-customer">
                                        <div className="flex items-center gap-2 px-1">
                                            <div className="h-2 w-2 rounded-full bg-sky-500"></div>
                                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">Waiting for Customer</h4>
                                            <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 h-4">{totalWaitingCustomer}</Badge>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {customerVerbs.map(verb => {
                                                const group = verbGroups[verb] || [];
                                                if (group.length === 0) return null;
                                                const cfg = ACTION_VERBS[verb];
                                                const onClick = () => {
                                                    if (group.length === 1) {
                                                        const job = group[0];
                                                        navigate(`/artwork-jobs/${job.id}`);
                                                    } else {
                                                        setView('active');
                                                        setStatusFilter('all');
                                                        setVerbFilter(verb);
                                                    }
                                                };
                                                return (
                                                    <button
                                                        key={verb}
                                                        type="button"
                                                        onClick={onClick}
                                                        className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-colors text-sm text-left"
                                                        data-testid={`attention-verb-${verb}`}
                                                    >
                                                        <div className={`h-2 w-2 rounded-full ${cfg.dotClass} flex-shrink-0`}></div>
                                                        <span className="text-slate-700 flex-1">
                                                            <span className="font-medium">{group.length}</span>{' '}
                                                            {cfg.label.toLowerCase()}
                                                        </span>
                                                        <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tabs + Filters */}
                <Card className="mb-6">
                    <CardContent className="pt-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            {/* Active / Completed Tabs */}
                            <div className="inline-flex bg-slate-100 p-1 rounded-lg" data-testid="job-view-tabs">
                                <button
                                    onClick={() => { setView('active'); setStatusFilter('all'); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                        view === 'active'
                                            ? 'bg-white text-slate-900 shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                    data-testid="tab-active"
                                >
                                    Active <span className="ml-1.5 text-xs text-slate-400">{activeJobs}</span>
                                </button>
                                <button
                                    onClick={() => { setView('completed'); setStatusFilter('all'); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                        view === 'completed'
                                            ? 'bg-white text-slate-900 shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                    data-testid="tab-completed"
                                >
                                    Completed <span className="ml-1.5 text-xs text-slate-400">{completedJobs}</span>
                                </button>
                                {isAdmin && (
                                    <button
                                        onClick={() => setView('settlement')}
                                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                            view === 'settlement'
                                                ? 'bg-white text-slate-900 shadow-sm'
                                                : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                        data-testid="tab-settlement"
                                    >
                                        Settlement
                                    </button>
                                )}
                            </div>

                            {/* Status filter (scoped to current tab) */}
                            {view === 'active' && (
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-[200px]" data-testid="status-filter">
                                        <SelectValue placeholder="All Statuses" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Statuses</SelectItem>
                                        {visibleStatusKeys.map((key) => (
                                            <SelectItem key={key} value={key}>{STATUS_CONFIG[key].label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}

                            {/* Active verb filter chip — set by the Needs Attention
                                panel above. Rendered here so partners can see what
                                the table is currently filtered by and clear it. */}
                            {verbFilter && (
                                <button
                                    type="button"
                                    onClick={() => setVerbFilter(null)}
                                    className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-full px-3 py-1 hover:bg-rose-100"
                                    data-testid="clear-verb-filter"
                                    title="Clear filter"
                                >
                                    {ACTION_VERBS[verbFilter]?.label || verbFilter}
                                    <span aria-hidden>×</span>
                                </button>
                            )}

                            <span className="text-sm text-slate-500">
                                {view === 'settlement'
                                    ? 'Reconciliation view — pick partner + period below.'
                                    : `Showing ${filteredJobs.length} of ${view === 'completed' ? completedJobs : activeJobs} ${view} job${(view === 'completed' ? completedJobs : activeJobs) !== 1 ? 's' : ''}`}
                            </span>
                        </div>
                    </CardContent>
                </Card>

                {view === 'settlement' ? (
                    <PartnerSettlementView />
                ) : (
                <>
                {/* Jobs Table */}
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-background">
                                    <TableHead className="w-[100px]">Order #</TableHead>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead className="w-[130px]">Status</TableHead>
                                    <TableHead className="w-[200px]">Action</TableHead>
                                    <TableHead className="w-[100px]">Files</TableHead>
                                    <TableHead className="w-[120px]">Created</TableHead>
                                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                                            Loading jobs...
                                        </TableCell>
                                    </TableRow>
                                ) : filteredJobs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                                            No jobs found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredJobs.map((job) => {
                                        const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG['Open'];
                                        const StatusIcon = statusConfig.icon;
                                        // Verb label — the actual next action for this job.
                                        // Colour coded by whose side acts: partner (rose/amber
                                        // for the two urgency tiers) vs customer (sky).
                                        const verbCfg = job.next_action ? ACTION_VERBS[job.next_action] : null;
                                        const verbColour = verbCfg?.side === 'partner'
                                            ? (verbCfg.dotClass === 'bg-rose-500'
                                                ? 'bg-rose-50 text-rose-700 border-rose-200'
                                                : 'bg-amber-50 text-amber-700 border-amber-200')
                                            : verbCfg?.side === 'customer'
                                                ? 'bg-sky-50 text-sky-700 border-sky-200'
                                                : '';
                                        
                                        return (
                                            <TableRow 
                                                key={job.id} 
                                                className="cursor-pointer hover:bg-background"
                                                onClick={() => navigate(`/artwork-jobs/${job.id}`)}
                                                data-testid={`job-row-${job.id}`}
                                            >
                                                <TableCell className="font-mono text-sm">
                                                    {String(job.order_number).padStart(6, '0')}
                                                </TableCell>
                                                <TableCell className="max-w-[280px]">
                                                    <div className="min-w-0">
                                                        <div className="flex items-baseline gap-2 min-w-0">
                                                            <span
                                                                className="font-medium truncate"
                                                                title={job.order_title}
                                                            >{job.order_title}</span>
                                                            {job.catalog_number && (
                                                                <span className="text-xs text-slate-400 shrink-0">
                                                                    ({job.catalog_number})
                                                                </span>
                                                            )}
                                                            <DemoBadge order={job} size="sm" />
                                                        </div>
                                                        {job.instructions && (
                                                            <p
                                                                className="text-xs text-slate-500 truncate max-w-[260px]"
                                                                title={job.instructions}
                                                            >
                                                                {job.instructions}
                                                            </p>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <Building2 className="h-3 w-3 text-slate-400" />
                                                        {job.organization_name || job.customer_name}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`${statusConfig.color} gap-1`}>
                                                        <StatusIcon className="h-3 w-3" />
                                                        {statusConfig.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {verbCfg ? (
                                                        <Badge
                                                            variant="outline"
                                                            className={`${verbColour} gap-1 whitespace-nowrap`}
                                                            data-testid={`row-verb-${job.next_action}`}
                                                            title={verbCfg.label}
                                                        >
                                                            {verbCfg.label}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                        <Palette className="h-3 w-3" />
                                                        {job.artwork_files?.length || 0}
                                                        <span className="text-slate-300">|</span>
                                                        <FileText className="h-3 w-3" />
                                                        {job.repro_files?.length || 0}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-slate-500">
                                                    {formatDate(job.created_at)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/artwork-jobs/${job.id}`);
                                                        }}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                        </div>
                    </CardContent>
                </Card>
                </>
                )}
            </main>
        </div>
    );
}
